/* eslint-disable react/no-unescaped-entities */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import useIsMobile from '@/hooks/useIsMobile';
import Footer from '@/components/Footer';

const PHONE = '+33674626368';
const PHONE_DISPLAY = '+33 6 74 62 63 68';
const WHATSAPP_URL = 'https://wa.me/33674626368';

const REVEAL_INITIAL  = { opacity: 0, y: 25 };
const REVEAL_ANIMATE  = { opacity: 1, y: 0 };
const REVEAL_VIEWPORT = { once: true };

const SectionReveal = ({ children, delay = 0 }) => {
  const isMobile = useIsMobile();
  if (isMobile) return <>{children}</>;
  return (
    <motion.div initial={REVEAL_INITIAL} whileInView={REVEAL_ANIMATE} viewport={REVEAL_VIEWPORT} transition={{ duration: 0.6, delay }}>
      {children}
    </motion.div>
  );
};

const Tarifs = () => {
  const { t } = useTranslation();

  const pricingRoutes = [
    { from: t('tarifs.airport_from', { defaultValue: "Aéroport d'Ajaccio" }), to: t('tarifs.ajaccio', { defaultValue: 'Centre-ville Ajaccio' }) },
    { from: t('tarifs.airport_from', { defaultValue: "Aéroport d'Ajaccio" }), to: 'Porticcio' },
    { from: t('tarifs.airport_from', { defaultValue: "Aéroport d'Ajaccio" }), to: 'Propriano' },
    { from: t('tarifs.airport_from', { defaultValue: "Aéroport d'Ajaccio" }), to: 'Sartène' },
    { from: t('tarifs.airport_from', { defaultValue: "Aéroport d'Ajaccio" }), to: 'Bonifacio' },
    { from: t('tarifs.airport_from', { defaultValue: "Aéroport d'Ajaccio" }), to: 'Porto-Vecchio' },
    { from: t('tarifs.airport_from', { defaultValue: "Aéroport d'Ajaccio" }), to: 'Bastia' },
    { from: t('tarifs.airport_from', { defaultValue: "Aéroport d'Ajaccio" }), to: 'Calvi' },
  ];

  useEffect(() => {
    document.title = t('tarifs.page_title', { defaultValue: 'Nos Tarifs | Taxi Porticcio | Yellow Cab Corsica' });
  }, [t]);

  return (
    <>
      <main className="bg-[#0A0A0A] min-h-screen pt-8 pb-28 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-[#555] mb-10">
            <Link to="/" className="hover:text-[#A3A3A3] transition-colors">{t('tarifs.breadcrumb_home', { defaultValue: 'Accueil' })}</Link>
            <span>/</span>
            <span className="text-[#A3A3A3]">{t('tarifs.breadcrumb', { defaultValue: 'Tarifs' })}</span>
          </div>

          {/* Header */}
          <SectionReveal>
            <div className="mb-12">
              <p className="text-xs uppercase tracking-[0.2em] text-[#EAB308] font-semibold mb-3">{t('tarifs.label')}</p>
              <h1 className="text-4xl sm:text-5xl font-light text-white tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('tarifs.title')}
              </h1>
              <p className="text-[#A3A3A3] text-base max-w-2xl">{t('tarifs.subtitle')}</p>
            </div>
          </SectionReveal>

          {/* Note */}
          <SectionReveal delay={0.05}>
            <div className="bg-[#EAB308]/5 border border-[#EAB308]/15 rounded-xl p-4 mb-10 text-sm text-[#A3A3A3]">
              {t('tarifs.note')}
            </div>
          </SectionReveal>

          {/* Routes */}
          <SectionReveal delay={0.1}>
            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('tarifs.routes_title')}
              </h2>
              <div className="bg-[#1A1A1A] border border-white/5 rounded-2xl overflow-hidden">
                {pricingRoutes.map((r, idx) => (
                  <div key={`${r.from}-${r.to}`}
                    className={`flex items-center justify-between px-6 py-4 ${idx !== pricingRoutes.length - 1 ? 'border-b border-white/5' : ''} hover:bg-white/2 transition-colors`}>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-[#A3A3A3]">{r.from}</span>
                      <ArrowRight size={14} className="text-[#555] shrink-0" />
                      <span className="text-white font-medium">{r.to}</span>
                    </div>
                    <span className="text-[#EAB308] font-semibold text-sm whitespace-nowrap ml-4">{t('tarifs.price')}</span>
                  </div>
                ))}
              </div>
            </div>
          </SectionReveal>

          {/* Cards */}
          <SectionReveal delay={0.15}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10">
              <div className="bg-[#1A1A1A] border border-white/5 rounded-2xl p-6">
                <h3 className="text-base font-semibold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {t('tarifs.disposal_title', { defaultValue: 'Mise à disposition' })}
                </h3>
                <p className="text-[#A3A3A3] text-sm mb-3">{t('tarifs.disposal_sub', { defaultValue: 'Votre chauffeur dédié' })}</p>
                <p className="text-2xl font-bold text-[#EAB308]">{t('tarifs.price')}</p>
                <p className="text-[#555] text-xs mt-2">{t('tarifs.disposal_note', { defaultValue: 'Minimum 2 heures · Kilométrage inclus' })}</p>
              </div>
              <div className="bg-[#1A1A1A] border border-white/5 rounded-2xl p-6">
                <h3 className="text-base font-semibold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {t('tarifs.events_title', { defaultValue: 'Événements & Mariages' })}
                </h3>
                <p className="text-[#A3A3A3] text-sm mb-3">{t('tarifs.events_sub', { defaultValue: 'Navettes sur mesure' })}</p>
                <p className="text-2xl font-bold text-[#EAB308]">{t('tarifs.on_quote', { defaultValue: 'Sur devis' })}</p>
                <p className="text-[#555] text-xs mt-2">{t('tarifs.events_note', { defaultValue: 'Devis gratuit · Réponse sous 24h' })}</p>
              </div>
            </div>
          </SectionReveal>

          {/* CTA */}
          <SectionReveal delay={0.2}>
            <div className="text-center bg-[#1A1A1A] border border-white/5 rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('tarifs.cta_title')}</h3>
              <p className="text-[#A3A3A3] text-sm mb-6">{t('tarifs.cta_subtitle')}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href={`tel:${PHONE}`} data-testid="tarifs-call-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#EAB308] hover:bg-[#CA8A04] text-black font-bold px-7 py-3.5 rounded-full text-sm transition-colors">
                  <Phone size={16} />{t('tarifs.call')} — {PHONE_DISPLAY}
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="tarifs-whatsapp-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-7 py-3.5 rounded-full text-sm transition-colors">
                  <MessageCircle size={16} />WhatsApp
                </a>
                <Link to="/#reservation"
                  className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/30 text-white px-7 py-3.5 rounded-full text-sm transition-all">
                  {t('tarifs.book')}
                </Link>
              </div>
            </div>
          </SectionReveal>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Tarifs;
